/**
 * External store for live side edge feature preview during slider drags.
 * Keyed by composite key `{edge}:{featureId}` to support multiple features.
 * Bypasses Redux so undo history is not polluted with intermediate values.
 *
 * Uses merge semantics: setPreviewSideEdge merges the incoming changes
 * with the existing preview for that key.
 */
import type {SideEdge, SideEdgeFeatureConfig} from '../types'

/** Stored preview values — a subset relevant for visual positioning */
export interface PreviewSideEdge {
  type: SideEdgeFeatureConfig['type']
  edge: SideEdge
  position: number
  width: number
  height: number
  cornerRadius: number
  diameter: number
  /** Side button protrude depth (signed offset of outer face from device surface) */
  depth: number
  /** Side button chamfer at the cutout entrance (mm) */
  chamfer: number
  /** SIM-slot eject pin-hole offset along the tray (mm) */
  pinHoleOffset: number
}

let snapshot: Record<string, PreviewSideEdge> | null = null
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((l) => l())

const buildKey = (edge: SideEdge, id: string) => `${edge}:${id}`

/**
 * Set (merge) preview for a specific side edge feature.
 * @param edge — edge name (left/right/top/bottom)
 * @param id — feature ID
 * @param base — full fallback values used when no preview record exists yet
 * @param changes — partial overrides to merge on top
 */
export const setPreviewSideEdge = (
  edge: SideEdge,
  id: string,
  base: PreviewSideEdge,
  changes: Partial<PreviewSideEdge>,
) => {
  const key = buildKey(edge, id)
  const existing = snapshot?.[key]
  snapshot = {
    ...snapshot,
    [key]: {
      type: base.type,
      edge: base.edge,
      position: changes.position ?? existing?.position ?? base.position,
      width: changes.width ?? existing?.width ?? base.width,
      height: changes.height ?? existing?.height ?? base.height,
      cornerRadius: changes.cornerRadius ?? existing?.cornerRadius ?? base.cornerRadius,
      diameter: changes.diameter ?? existing?.diameter ?? base.diameter,
      depth: changes.depth ?? existing?.depth ?? base.depth,
      chamfer: changes.chamfer ?? existing?.chamfer ?? base.chamfer,
      pinHoleOffset: changes.pinHoleOffset ?? existing?.pinHoleOffset ?? base.pinHoleOffset,
    },
  }
  notify()
}

export const clearPreviewSideEdge = () => {
  snapshot = null
  notify()
}

export const getPreviewSideEdgeSnapshot = (): Record<string, PreviewSideEdge> | null => snapshot

export const subscribePreviewSideEdge = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
